import { useState, useEffect, FocusEvent, FC, useMemo, useCallback, useContext } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Formik, useFormikContext } from 'formik';
import moment from 'moment';
import { TableCustom } from '../../../component/table/table-custom/TableCustom';
import { CellProps, Column } from 'react-table';
import { TableCustomCell } from '../../../component/table/components/TableCustomCell';
import { TableCustomHeader } from '../../../component/table/components/TableCustomHeader';
import CellFormatter from '../../../component/cell-formatter';
import AutocompleteV2 from '../../../component/AutocompleteObjectV2';
import DatePickerCustom from '../../../component/date-picker/DatePickerCustom';
import { CODE, SELECTION_MODE } from '../../../utils/Constant';
import { formatDateTime } from '../../../utils/FormatUtils';
import { getSurgeyGroupsByVisit, executeBatchSurgeryOrders } from '../../services/PhanHeChuyenKhoaService';
import { AppContext } from '../../../appContext/AppContext';
import { toast } from 'react-toastify';
import { getDSBacSi } from '../../../phan-he-kham-benh/services/KhamBenhService';

type Props = {
  open: boolean;
  handleClose: () => void;
  wfData?: any;
  onUpdate?: () => void;
};

// Interface cho dữ liệu phiếu thực hiện (theo API response)
interface IPhieuThucHien {
  surgeryGroupCode: string;
  indicationTime: string;
  // Thêm các fields cho edit
  obs: {
    surgeryStartTime: string | Date | null;
    surgeryEndTime: string | Date | null;
    surgeryPrimarySurgeon1st: string | null;
    surgeryPrimarySurgeon1stId: string | null;
    surgeryPrimarySurgeon1stName: string | null;
    surgerySecondarySurgeon1st?: string | null;
    surgerySecondarySurgeon1stId?: string | null;
    surgerySecondarySurgeon1stName?: string | null;
    surgerySecondarySurgeon2nd?: string | null;
    surgerySecondarySurgeon2ndId?: string | null;
    surgerySecondarySurgeon2ndName?: string | null;
    executionDuration?: number | string | null;
    minExecutionMinutes?: number | string | null;
    maxExecutionMinutes?: number | string | null;
    surgeryPreoperativeDiagnosisText?: string | null;
    surgeryInitialDiagnosisText?: string | null;
    surgeryPreoperativeDiagnosis?: string | null;
    surgeryInitialDiagnosis?: string | null;
    surgerySurgicalProcedure?: string | null;
    surgerySurgicalDescription?: string | null;
    surgeryInformationEntryDate?: string | null;
    surgeryInformationEntryDepartment?: string | null;
    surgeryInformationEntryDepartmentId?: string | null;
    surgeryInformationEntryDepartmentName?: string | null;
    surgeryInformationEntryPerson?: string | null;
    surgeryInformationEntryRoom?: string | null;
    surgeryInformationEntryRoomId?: string | null;
    surgeryInformationEntryRoomName?: string | null;
    patientSignature?: string | null;
    [key: string]: any;
  };
  // Thêm các fields cho view
  [key: string]: any;
}

// Interface cho người thực hiện
interface INguoiThucHien {
  id: string;
  fullName: string;
}

const COLUMN_ID = {
  THOI_GIAN_BAT_DAU: 'THOI_GIAN_BAT_DAU',
  THOI_GIAN_KET_THUC: 'THOI_GIAN_KET_THUC',
  NGUOI_THUC_HIEN: 'NGUOI_THUC_HIEN',
  KTV_PHU_1: 'KTV_PHU_1',
  KTV_PHU_2: 'KTV_PHU_2',
};

const ModalDsPhieuThucHien = ({ open, handleClose, wfData = {}, onUpdate }: Props) => {
  const { termId, visitId, personId } = wfData;
  const { setIsLoading } = useContext(AppContext);

  // State quản lý dữ liệu table
  const [tableData, setTableData] = useState<IPhieuThucHien[]>([]);

  // State quản lý các row được chọn (lưu cả object row)
  const [selectedRows, setSelectedRows] = useState<IPhieuThucHien[]>([]);

  // State quản lý cell đang được edit
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedCol, setSelectedCol] = useState<any>(null);

  // State quản lý danh sách người thực hiện
  const [dsNguoiThucHien, setDsNguoiThucHien] = useState<INguoiThucHien[]>([]);
  // Hàm gọi API lấy danh sách phiếu thực hiện
  const fetchSurgeryGroups = useCallback(async () => {
    if (!visitId || !termId) {
      console.warn('Missing required params: visitId, termId, or statusCode');
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await getSurgeyGroupsByVisit(visitId, { termId });

      if (CODE.SUCCESS === data?.code && data?.data) {
        // Sử dụng trực tiếp data từ API
        setTableData(data.data);
        setSelectedRows([]);
      } else {
        toast.error(data?.message || 'Lỗi khi lấy danh sách phiếu thực hiện');
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching surgery groups:', error);
      toast.error('Có lỗi xảy ra khi tải dữ liệu');
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [visitId, termId, setIsLoading]);

  // Hàm lấy danh sách người thực hiện
  const getDsNguoiThucHien = useCallback(async () => {
    try {
      const { data } = await getDSBacSi({
        pageIndex: 1,
        pageSize: 9999,
        roleCode: 'KTVPTTT',
      });

      if (CODE.SUCCESS === data?.code) {
        setDsNguoiThucHien(data?.data?.content || []);
      }
    } catch (error) {
      console.error('Error fetching nguoi thuc hien:', error);
    }
  }, []);

  // Khởi tạo danh sách người thực hiện khi component mount
  useEffect(() => {
    getDsNguoiThucHien();
  }, [getDsNguoiThucHien]);

  // Khởi tạo data khi mở modal
  useEffect(() => {
    if (open) {
      fetchSurgeryGroups();
    }
  }, [open, fetchSurgeryGroups]);

  // Hàm xử lý update dữ liệu trong table (không gọi API, chỉ update state)
  // Giống như handleSaveObs trong BangDsDichVuChuyenKhoa nhưng không gọi API
  const handleUpdateRow = useCallback((rowData: IPhieuThucHien, value: any) => {
    setTableData((prevData) =>
      prevData.map((item) => {
        if (item.id === rowData.id) {
          const updatedObs = {
            ...item.obs,
            ...value,
          };

          // Nếu surgeryStartTime được cập nhật, tự động tính toán surgeryEndTime
          if (value?.surgeryStartTime) {
            // Ép kiểu surgeryStartTime sang moment và kiểm tra valid
            const startTime = moment(value.surgeryStartTime);
            if (startTime.isValid()) {
              // Hàm parse number an toàn, trả về null nếu không hợp lệ
              const parseNumber = (val: any): number | null => {
                if (val === null || val === undefined || val === '') return null;
                const num = Number(val);
                return isNaN(num) ? null : num;
              };

              // Lấy duration, dùng ?? để xử lý trường hợp giá trị = 0
              const executionDuration = parseNumber(item.obs?.executionDuration);
              const minExecutionMinutes = parseNumber(item.obs?.minExecutionMinutes);
              const maxExecutionMinutes = parseNumber(item.obs?.maxExecutionMinutes);
              const durationInMinutes = executionDuration ?? minExecutionMinutes ?? maxExecutionMinutes ?? 5;

              // Clone trước khi add để tránh mutate object gốc
              const endTime = startTime.clone().add(durationInMinutes, 'minutes');

              // Tự động update surgeryEndTime - dùng format() để giữ format nhất quán với surgeryStartTime (local time)
              updatedObs.surgeryEndTime = endTime.format('YYYY-MM-DDTHH:mm:ss');
            }
          }

          return {
            ...item,
            obs: updatedObs,
          };
        }
        return item;
      })
    );
  }, []);

  // Hàm xử lý khi selection thay đổi
  const handleSelectionChange = useCallback((rows: IPhieuThucHien[]) => {
    setSelectedRows(rows);
  }, []);

  // Hàm xử lý submit
  const handleSubmit = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Vui lòng chọn ít nhất một phiếu để thực hiện');
      return;
    }

    setIsLoading(true);
    try {
      // Chuẩn bị data theo format API yêu cầu
      const formRequests = selectedRows.map((row) => ({
        personId,
        orderId: row.orderId,
        values: row.obs || {}, // Lấy toàn bộ object obs
      }));

      const submitData = {
        formRequests,
      };

      const { data } = await executeBatchSurgeryOrders(submitData);

      if (CODE.SUCCESS === data?.code) {
        toast.success('Thực hiện phiếu thành công');
        onUpdate?.();
        handleClose();
        // Có thể callback để refresh data ở component cha
      } else {
        toast.error(data?.message || 'Lỗi khi thực hiện phiếu');
      }
    } catch (error) {
      console.error('Error executing batch surgery orders:', error);
      toast.error('Có lỗi xảy ra khi thực hiện phiếu');
    } finally {
      setIsLoading(false);
    }
  };

  // Định nghĩa các cột của table - dùng useMemo để tránh re-create mỗi lần render
  const columns: ReadonlyArray<Column<IPhieuThucHien>> = useMemo(() => [
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="STT"
          className="text-center text-white align-middle bg-pri"
        />
      ),
      id: 'STT',
      Cell: ({ row }: CellProps<IPhieuThucHien>) => (
        <TableCustomCell
          data={<CellFormatter rowData={row} value={row.index + 1} />}
          className="text-center fs-14"
        />
      ),
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="MÃ PHIẾU"
          className="text-center text-white align-middle bg-pri min-w-100px"
        />
      ),
      id: 'surgeryGroupCode',
      Cell: ({ row: { original } }: CellProps<IPhieuThucHien>) => (
        <TableCustomCell
          data={<CellFormatter rowData={original} value={original.surgeryGroupCode} />}
          className="text-center fs-14"
        />
      ),
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="NGÀY CHỈ ĐỊNH"
          className="text-center text-white align-middle bg-pri min-w-150px"
        />
      ),
      id: 'indicationTime',
      Cell: ({ row: { original } }: CellProps<IPhieuThucHien>) => (
        <TableCustomCell
          data={
            <CellFormatter
              rowData={original}
              value={formatDateTime(original.indicationTime)}
            />
          }
          className="text-center fs-14"
        />
      ),
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="THỜI GIAN BẮT ĐẦU"
          className="text-center text-white align-middle bg-pri spaces min-w-175"
        />
      ),
      id: COLUMN_ID.THOI_GIAN_BAT_DAU,
      Cell: ({ ...props }) => {
        const rowData = props?.data[props?.row?.index];
        const colData = props?.column;
        const propsInput = {
          rowData,
          colData,
          dsNguoiThucHien,
          handleUpdateRow,
          setSelectedRow,
          setSelectedCol,
        };
        const editable =
          selectedRow?.index === props?.row?.index && colData?.id === selectedCol?.id;

        return (
          <>
            {editable ? (
              <GenerateInput {...propsInput} />
            ) : (
              <div
                className="text-system spaces h-28 line-height-28 text-center cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow?.({ ...rowData, index: props?.row?.index });
                  setSelectedCol?.(colData);
                }}
              >
                {formatDateTime(rowData?.obs?.surgeryStartTime) || ''}
              </div>
            )}
          </>
        );
      },
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="THỜI GIAN KẾT THÚC"
          className="text-center text-white align-middle bg-pri spaces min-w-175"
        />
      ),
      id: COLUMN_ID.THOI_GIAN_KET_THUC,
      Cell: ({ ...props }) => {
        const rowData = props?.data[props?.row?.index];
        const colData = props?.column;
        const propsInput = {
          rowData,
          colData,
          dsNguoiThucHien,
          handleUpdateRow,
          setSelectedRow,
          setSelectedCol,
        };
        const editable =
          selectedRow?.index === props?.row?.index && colData?.id === selectedCol?.id;

        return (
          <>
            {editable ? (
              <GenerateInput {...propsInput} />
            ) : (
              <div
                className="text-system spaces h-28 line-height-28 text-center cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow?.({ ...rowData, index: props?.row?.index });
                  setSelectedCol?.(colData);
                }}
              >
                {formatDateTime(rowData?.obs?.surgeryEndTime) || ''}
              </div>
            )}
          </>
        );
      },
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="NGƯỜI THỰC HIỆN"
          className="text-center text-white align-middle bg-pri spaces min-w-175"
        />
      ),
      id: COLUMN_ID.NGUOI_THUC_HIEN,
      Cell: ({ ...props }) => {
        const rowData = props?.data[props?.row?.index];
        const colData = props?.column;
        const propsInput = {
          rowData,
          colData,
          dsNguoiThucHien,
          handleUpdateRow,
          setSelectedRow,
          setSelectedCol,
        };
        const editable =
          selectedRow?.index === props?.row?.index && colData?.id === selectedCol?.id;

        return (
          <>
            {editable ? (
              <GenerateInput {...propsInput} />
            ) : (
              <div
                className="text-system spaces h-28 line-height-28 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow?.({ ...rowData, index: props?.row?.index });
                  setSelectedCol?.(colData);
                }}
              >
                {rowData?.obs?.surgeryPrimarySurgeon1stName || ''}
              </div>
            )}
          </>
        );
      },
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="KTV PHỤ 1"
          className="text-center text-white align-middle bg-pri spaces min-w-175"
        />
      ),
      id: COLUMN_ID.KTV_PHU_1,
      Cell: ({ ...props }) => {
        const rowData = props?.data[props?.row?.index];
        const colData = props?.column;
        const propsInput = {
          rowData,
          colData,
          dsNguoiThucHien,
          handleUpdateRow,
          setSelectedRow,
          setSelectedCol,
        };
        const editable =
          selectedRow?.index === props?.row?.index && colData?.id === selectedCol?.id;

        return (
          <>
            {editable ? (
              <GenerateInput {...propsInput} />
            ) : (
              <div
                className="text-system spaces h-28 line-height-28 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow?.({ ...rowData, index: props?.row?.index });
                  setSelectedCol?.(colData);
                }}
              >
                {rowData?.obs?.surgerySecondarySurgeon1stName || ''}
              </div>
            )}
          </>
        );
      },
    },
    {
      Header: (props) => (
        <TableCustomHeader<IPhieuThucHien>
          tableProps={props}
          title="KTV PHỤ 2"
          className="text-center text-white align-middle bg-pri spaces min-w-175"
        />
      ),
      id: COLUMN_ID.KTV_PHU_2,
      Cell: ({ ...props }) => {
        const rowData = props?.data[props?.row?.index];
        const colData = props?.column;
        const propsInput = {
          rowData,
          colData,
          dsNguoiThucHien,
          handleUpdateRow,
          setSelectedRow,
          setSelectedCol,
        };
        const editable =
          selectedRow?.index === props?.row?.index && colData?.id === selectedCol?.id;

        return (
          <>
            {editable ? (
              <GenerateInput {...propsInput} />
            ) : (
              <div
                className="text-system spaces h-28 line-height-28 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow?.({ ...rowData, index: props?.row?.index });
                  setSelectedCol?.(colData);
                }}
              >
                {rowData?.obs?.surgerySecondarySurgeon2ndName || ''}
              </div>
            )}
          </>
        );
      },
    },
  ], [selectedRow, selectedCol, handleUpdateRow, setSelectedRow, setSelectedCol, dsNguoiThucHien]); // Dependencies: chỉ re-create khi cần thiết

  return (
    <Modal
      show={open}
      onHide={handleClose}
      animation
      centered
      className="dialog-background"
      dialogClassName="modal-xl"
    >
      <Modal.Header className="py-3 header-modal">
        <Modal.Title className="text-pri">Danh sách phiếu thực hiện</Modal.Title>
        <button className="btn-close" onClick={handleClose}></button>
      </Modal.Header>
      <Modal.Body className="py-16 px-24 spaces" style={{ minHeight: '400px' }}>
        <Formik initialValues={{}} onSubmit={() => {}}>
          <TableCustom<IPhieuThucHien>
            className="h-100"
            data={tableData}
            columns={columns}
            selectionMode={SELECTION_MODE.MULTI}
            verticalScroll={true}
            rowSelected={selectedRows}
            getSelectedRowsData={handleSelectionChange}
            autoResetSelectedRows={false}
          />
        </Formik>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end py-1">
        <Button className="btn-outline me-2" onClick={handleClose}>
          <span>Đóng</span>
        </Button>
        <Button
          className="btn-fill"
          type="submit"
          onClick={handleSubmit}
          disabled={selectedRows.length === 0}
        >
          <span>Lưu ({selectedRows.length})</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDsPhieuThucHien;

// Component tạo input cho các cột có thể nhập
const GenerateInput: FC<any> = ({
  rowData,
  colData,
  dsNguoiThucHien,
  handleUpdateRow,
  setSelectedRow,
  setSelectedCol,
}) => {
  const { values, setFieldValue, setValues } = useFormikContext<any>() || {};

  useEffect(() => {
    setValues({
      ...values,
      surgeryStartTime: rowData?.obs?.surgeryStartTime,
      surgeryEndTime: rowData?.obs?.surgeryEndTime,
      nguoiThucHien: rowData?.obs?.surgeryPrimarySurgeon1stId
        ? {
            id: rowData?.obs?.surgeryPrimarySurgeon1stId,
            fullName: rowData?.obs?.surgeryPrimarySurgeon1stName,
          }
        : null,
      ktvPhu1: rowData?.obs?.surgerySecondarySurgeon1stId
        ? {
            id: rowData?.obs?.surgerySecondarySurgeon1stId,
            fullName: rowData?.obs?.surgerySecondarySurgeon1stName,
          }
        : null,
      ktvPhu2: rowData?.obs?.surgerySecondarySurgeon2ndId
        ? {
            id: rowData?.obs?.surgerySecondarySurgeon2ndId,
            fullName: rowData?.obs?.surgerySecondarySurgeon2ndName,
          }
        : null,
    });
  }, []);

  switch (colData?.id) {
    case COLUMN_ID.THOI_GIAN_BAT_DAU:
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <DatePickerCustom
            autoFocus
            name="surgeryStartTime"
            labelClassName="ms-0 min-w-100px"
            value={values?.surgeryStartTime}
            setDateValue={(date) => {
              setFieldValue?.('surgeryStartTime', date);
            }}
            {...{
              [values?.surgeryStartTime ? 'handleBlur' : 'onClickOutside']: () => {
                handleUpdateRow(
                  rowData,
                  values?.surgeryStartTime
                    ? { surgeryStartTime: values?.surgeryStartTime }
                    : null
                );
                setSelectedRow?.(null);
                setSelectedCol?.(null);
              },
            }}
            mode="date-time"
            popperPlacement="left"
          />
        </div>
      );

    case COLUMN_ID.THOI_GIAN_KET_THUC:
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <DatePickerCustom
            autoFocus
            name="surgeryEndTime"
            labelClassName="ms-0 min-w-100px"
            value={values?.surgeryEndTime}
            setDateValue={(date) => {
              setFieldValue?.('surgeryEndTime', date);
            }}
            {...{
              [values?.surgeryEndTime ? 'handleBlur' : 'onClickOutside']: () => {
                handleUpdateRow(
                  rowData,
                  values?.surgeryEndTime
                    ? { surgeryEndTime: values?.surgeryEndTime }
                    : null
                );
                setSelectedRow?.(null);
                setSelectedCol?.(null);
              },
            }}
            mode="date-time"
            popperPlacement="left"
          />
        </div>
      );

    case COLUMN_ID.NGUOI_THUC_HIEN:
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <AutocompleteV2
            autoFocus
            options={dsNguoiThucHien}
            value={values?.nguoiThucHien || null}
            name="nguoiThucHien"
            getOptionLabel={(option) => option?.fullName}
            onChange={(selectedOption) => {
              setFieldValue?.('nguoiThucHien', selectedOption);
            }}
            onBlur={() => {
              handleUpdateRow(
                rowData,
                values?.nguoiThucHien
                  ? {
                      surgeryPrimarySurgeon1st: values?.nguoiThucHien?.id,
                      surgeryPrimarySurgeon1stId: values?.nguoiThucHien?.id,
                      surgeryPrimarySurgeon1stName: values?.nguoiThucHien?.fullName,
                    }
                  : null
              );
              setSelectedRow?.(null);
              setSelectedCol?.(null);
            }}
            className="spaces width-100 h-28"
            isClearable={false}
          />
        </div>
      );

    case COLUMN_ID.KTV_PHU_1:
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <AutocompleteV2
            autoFocus
            options={dsNguoiThucHien}
            value={values?.ktvPhu1 || null}
            name="ktvPhu1"
            getOptionLabel={(option) => option?.fullName}
            onChange={(selectedOption) => {
              setFieldValue?.('ktvPhu1', selectedOption);
            }}
            onBlur={() => {
              handleUpdateRow(
                rowData,
                values?.ktvPhu1
                  ? {
                      surgerySecondarySurgeon1st: values?.ktvPhu1?.id,
                      surgerySecondarySurgeon1stId: values?.ktvPhu1?.id,
                      surgerySecondarySurgeon1stName: values?.ktvPhu1?.fullName,
                    }
                  : null
              );
              setSelectedRow?.(null);
              setSelectedCol?.(null);
            }}
            className="spaces width-100 h-28"
            isClearable={false}
          />
        </div>
      );

    case COLUMN_ID.KTV_PHU_2:
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <AutocompleteV2
            autoFocus
            options={dsNguoiThucHien}
            value={values?.ktvPhu2 || null}
            name="ktvPhu2"
            getOptionLabel={(option) => option?.fullName}
            onChange={(selectedOption) => {
              setFieldValue?.('ktvPhu2', selectedOption);
            }}
            onBlur={() => {
              handleUpdateRow(
                rowData,
                values?.ktvPhu2
                  ? {
                      surgerySecondarySurgeon2nd: values?.ktvPhu2?.id,
                      surgerySecondarySurgeon2ndId: values?.ktvPhu2?.id,
                      surgerySecondarySurgeon2ndName: values?.ktvPhu2?.fullName,
                    }
                  : null
              );
              setSelectedRow?.(null);
              setSelectedCol?.(null);
            }}
            className="spaces width-100 h-28"
            isClearable={false}
          />
        </div>
      );

    default:
      return null;
  }
};
